import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@katsquare/types';
import { Button, Input, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { ApiRequestError, getAccountStorage, updateProfile } from '../lib/api';
import { formatBytes } from '../lib/format';
import { SESSION_KEY, useSession } from '../features/auth/useSession';

export function ProfilePage() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(user?.name ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // Re-sync if the session's name changes out from under the form (e.g. a
  // refetch after saving elsewhere) so `canSave` compares against reality.
  useEffect(() => {
    setName(user?.name ?? '');
  }, [user?.name]);

  const save = useMutation<SessionUser, ApiRequestError>({
    mutationFn: () => {
      const nameChanged = name.trim() && name.trim() !== user?.name;
      return updateProfile({
        ...(nameChanged ? { name: name.trim() } : {}),
        ...(newPassword ? { currentPassword, newPassword } : {}),
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(SESSION_KEY, updated);
      setCurrentPassword('');
      setNewPassword('');
      toast('Profile updated');
    },
  });

  const storage = useQuery({ queryKey: ['accountStorage'], queryFn: getAccountStorage });

  const avatar = useMutation({
    mutationFn: (avatarUrl: string) => updateProfile({ avatarUrl }),
    onSuccess: (u) => {
      queryClient.setQueryData(SESSION_KEY, u);
      toast(u.avatarUrl ? 'Photo updated' : 'Photo removed');
    },
    onError: (e: Error) => toast(e.message),
  });

  const initials = (user?.name ?? '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const nameChanged = name.trim().length > 0 && name.trim() !== user?.name;
  const canSave = nameChanged || newPassword.length > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-6 py-3.5">
        <h1 className="text-lg font-semibold tracking-tight">Your profile</h1>
        <Link to="/app" className="ml-auto text-sm text-neutral-500 hover:text-neutral-900">
          ← Back
        </Link>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto max-w-lg space-y-6">
          <div className="flex items-center gap-4">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xl font-semibold text-white">
                {initials}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-neutral-900">{user?.name}</p>
              <p className="truncate text-xs text-neutral-500">{user?.email}</p>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-700">
                  {avatar.isPending ? 'Uploading…' : 'Upload photo'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      try {
                        avatar.mutate(await resizeToDataUrl(f));
                      } catch {
                        toast('Could not read that image.');
                      }
                    }}
                  />
                </label>
                {user?.avatarUrl && (
                  <button
                    type="button"
                    onClick={() => avatar.mutate('')}
                    className="text-xs text-neutral-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <section className="rounded-xl border border-neutral-200 bg-white p-5">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-neutral-900">Storage</h2>
              <Link to="/pricing" className="text-xs font-medium text-blue-600 hover:text-blue-700">
                Manage plan
              </Link>
            </div>
            {storage.isPending ? (
              <div className="py-2">
                <Spinner />
              </div>
            ) : storage.data ? (
              (() => {
                const { usedBytes, limitBytes } = storage.data;
                const pct = limitBytes ? (usedBytes / limitBytes) * 100 : 0;
                const bar = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium text-neutral-700">{formatBytes(usedBytes)} used</span>
                      <span className="text-neutral-400">
                        {limitBytes == null ? 'Unlimited' : `of ${formatBytes(limitBytes)}`}
                      </span>
                    </div>
                    {limitBytes != null && (
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                        <div
                          className={`h-full rounded-full ${bar}`}
                          style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
                        />
                      </div>
                    )}
                    {limitBytes != null && pct >= 90 && (
                      <p className="mt-2 text-xs text-red-600">
                        You’re almost out of storage.{' '}
                        <Link to="/pricing" className="font-medium underline">
                          Upgrade
                        </Link>{' '}
                        for more room.
                      </p>
                    )}
                  </>
                );
              })()
            ) : (
              <p className="text-sm text-neutral-400">Couldn’t load storage usage.</p>
            )}
          </section>

          <form
            className="space-y-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (canSave) save.mutate();
            }}
          >
            <section className="rounded-xl border border-neutral-200 bg-white p-5">
              <h2 className="mb-3 text-sm font-semibold text-neutral-900">Details</h2>
              <label className="mb-1 block text-xs font-medium text-neutral-500">Display name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                className="w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm outline-none focus:border-neutral-900"
              />
              <label className="mb-1 mt-3 block text-xs font-medium text-neutral-500">Email</label>
              <input
                value={user?.email ?? ''}
                disabled
                className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm text-neutral-500"
              />
            </section>

            <section className="rounded-xl border border-neutral-200 bg-white p-5">
              <h2 className="mb-3 text-sm font-semibold text-neutral-900">Change password</h2>
              <label className="mb-1 block text-xs font-medium text-neutral-500">Current password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
              <label className="mb-1 mt-3 block text-xs font-medium text-neutral-500">
                New password <span className="text-neutral-400">(min 10 characters)</span>
              </label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
              />
            </section>

            {save.isError && <p className="text-sm text-red-500">{save.error.message}</p>}

            <Button type="submit" disabled={!canSave || save.isPending}>
              {save.isPending ? <Spinner /> : 'Save changes'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

/** Resize an image file to a square JPEG data URL for use as an avatar. */
async function resizeToDataUrl(file: File, size = 160): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('load failed'));
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas context');
    const scale = Math.max(size / img.width, size / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
